import React, { useEffect } from 'react';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Divider,
    Grid,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import {
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { useDashboardController } from './context/dashboard-controller';
import { useDashboardState } from './context/dashboard-context';
import PageHeader from '../../../components/page-header';

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export function DashboardComponent() {
    const theme = useTheme();
    const { loadData } = useDashboardController();
    const { dashboardData, loading } = useDashboardState();

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const COLORS = [
        theme.palette.primary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
    ];

    const getGrowthColor = (pct: number) => {
        if (pct > 0) return 'success.main';
        if (pct < 0) return 'error.main';
        return 'text.secondary';
    };

    const getGrowthIcon = (pct: number) => {
        if (pct > 0) return <TrendingUpIcon fontSize="small" />;
        if (pct < 0) return <TrendingDownIcon fontSize="small" />;
        return null;
    };

    const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
        if (percent < 0.05) return null;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
        const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
        return (
            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontWeight="bold" fontSize="14">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    const statusDistributionData = (dashboardData?.statusDistribution ?? []).map((item, index) => ({
        ...item,
        color: COLORS[index % COLORS.length],
    }));

    const amendmentStats = dashboardData?.amendmentStatistics;
    const commitmentStats = dashboardData?.commitmentStatistics;
    const liquidationStats = dashboardData?.liquidationStatistics;
    const paymentStats = dashboardData?.paymentStatistics;

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Dashboard"
                    subtitle="Visão geral da gestão de emendas parlamentares"
                    breadcrumbs={[{ label: 'Dashboard' }]}
                />
            </Grid>

            {/* Cards de resumo */}
            <Grid item xs={12}>
                <Grid container spacing={3}>
                    {/* Emendas */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                            <CardContent>
                                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">
                                            EMENDAS
                                        </Typography>
                                        <Typography color="textPrimary" variant="h3">
                                            {amendmentStats?.totalAmendments ?? 0}
                                        </Typography>
                                        {amendmentStats && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: getGrowthColor(amendmentStats.growthPercentage), display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                >
                                                    {getGrowthIcon(amendmentStats.growthPercentage)}
                                                    {Math.abs(amendmentStats.growthPercentage).toFixed(1)}%
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                                                    vs ano anterior
                                                </Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item>
                                        <Avatar sx={{ backgroundColor: 'primary.main', height: 56, width: 56 }}>
                                            <AccountBalanceIcon />
                                        </Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="textSecondary">
                                    Valor total: {formatCurrency(amendmentStats?.totalValue ?? 0)}
                                </Typography>
                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <HourglassEmptyIcon fontSize="small" color="disabled" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Não iniciadas</Typography>
                                                <Typography variant="body2" fontWeight="bold">{amendmentStats?.notStartedAmendments ?? 0}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PlayCircleIcon fontSize="small" color="warning" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Em andamento</Typography>
                                                <Typography variant="body2" fontWeight="bold">{amendmentStats?.inProgressAmendments ?? 0}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CheckCircleIcon fontSize="small" color="success" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Concluídas</Typography>
                                                <Typography variant="body2" fontWeight="bold">{amendmentStats?.completedAmendments ?? 0}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CancelIcon fontSize="small" color="error" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Canceladas</Typography>
                                                <Typography variant="body2" fontWeight="bold">{amendmentStats?.cancelledAmendments ?? 0}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Empenhos */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                            <CardContent>
                                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">
                                            EMPENHOS
                                        </Typography>
                                        <Typography color="textPrimary" variant="h3">
                                            {commitmentStats?.totalCommitments ?? 0}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                            {commitmentStats?.currentYearCommitments ?? 0} no ano atual
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Avatar sx={{ backgroundColor: 'success.main', height: 56, width: 56 }}>
                                            <AssignmentTurnedInIcon />
                                        </Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="textSecondary">Valor empenhado</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {formatCurrency(commitmentStats?.totalCommittedValue ?? 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Liquidações */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                            <CardContent>
                                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">
                                            LIQUIDAÇÕES
                                        </Typography>
                                        <Typography color="textPrimary" variant="h3">
                                            {liquidationStats?.totalLiquidations ?? 0}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Avatar sx={{ backgroundColor: 'warning.main', height: 56, width: 56 }}>
                                            <ReceiptIcon />
                                        </Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="textSecondary">Valor liquidado</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {formatCurrency(liquidationStats?.totalLiquidatedValue ?? 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Pagamentos */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                            <CardContent>
                                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">
                                            PAGAMENTOS
                                        </Typography>
                                        <Typography color="textPrimary" variant="h3">
                                            {paymentStats?.totalPayments ?? 0}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Avatar sx={{ backgroundColor: 'info.main', height: 56, width: 56 }}>
                                            <PaymentsIcon />
                                        </Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="textSecondary">Valor pago</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {formatCurrency(paymentStats?.totalPaidValue ?? 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            {/* Gráfico de linha — Timeline */}
            <Grid item xs={12} md={7}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Linha do Tempo — Emendas e Empenhos
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {dashboardData?.timeline && dashboardData.timeline.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={dashboardData.timeline} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" style={{ fontSize: '12px' }} />
                                    <YAxis style={{ fontSize: '12px' }} allowDecimals={false} />
                                    <Tooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Paper sx={{ p: 1.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">{payload[0].payload.period}</Typography>
                                                    {payload.map((entry: any, i: number) => (
                                                        <Typography key={i} variant="body2" sx={{ color: entry.color }}>
                                                            {entry.name}: {entry.value}
                                                        </Typography>
                                                    ))}
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    }} />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="amendmentCount"
                                        stroke={theme.palette.primary.main}
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        name="Emendas"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="commitmentCount"
                                        stroke={theme.palette.success.main}
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        name="Empenhos"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                {loading ? 'Carregando...' : 'Sem dados para o período'}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* Gráfico de pizza — Distribuição por status */}
            <Grid item xs={12} md={5}>
                <Card sx={{ height: '100%' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Distribuição por Status
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {statusDistributionData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={statusDistributionData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={CustomPieLabel}
                                        outerRadius={100}
                                        dataKey="count"
                                    >
                                        {statusDistributionData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            const item = payload[0].payload;
                                            return (
                                                <Paper sx={{ p: 1.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">{item.name}</Typography>
                                                    <Typography variant="body2">Quantidade: {item.count}</Typography>
                                                    <Typography variant="body2">Percentual: {item.percentage.toFixed(1)}%</Typography>
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    }} />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={36}
                                        formatter={(value, entry: any) => `${value}: ${entry.payload.count}`}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                {loading ? 'Carregando...' : 'Sem emendas cadastradas'}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </React.Fragment>
    );
}
